import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../../_services/alert.service';
import { DeliveryPartner, DeliveryPartnersService } from '../../../_services/delivery-partners.service';

// Delivery Partners: the couriers/hauliers staff pick from when closing out
// an order in Validate Orders. Laid out like Postcodes.
@Component({
    selector: 'app-delivery-partners',
    imports: [MainLayoutComponent, CommonModule, FormsModule, MatIconModule, MatTooltipModule],
    templateUrl: './delivery-partners.component.html',
    styleUrl: '../../postcodes/postcodes.component.css'
})
export class DeliveryPartnersComponent implements OnInit {
  partners: DeliveryPartner[] = [];
  loading = true;
  loadError = '';

  editing: DeliveryPartner | null = null;
  saving = false;
  formError = '';
  deleting: DeliveryPartner | null = null;
  removing = false;

  constructor(private service: DeliveryPartnersService, private alertService: AlertService) {}

  ngOnInit(): void {
    this.load();
  }

  get activeCount(): number {
    return this.partners.filter((p) => p.active).length;
  }

  get ordersCount(): number {
    return this.partners.reduce((sum, p) => sum + (p.orders ?? 0), 0);
  }

  load() {
    this.loading = true;
    this.loadError = '';
    this.service.list(true).subscribe({
      next: (rows) => {
        this.partners = rows || [];
        this.loading = false;
      },
      error: (e) => {
        this.loading = false;
        this.partners = [];
        this.loadError = e?.error?.message || 'Could not load delivery partners.';
      },
    });
  }

  openAdd() {
    this.formError = '';
    this.editing = { name: '', phone: '', trackingUrl: '', active: true };
  }

  openEdit(p: DeliveryPartner) {
    this.formError = '';
    this.editing = { ...p };
  }

  closeForm() {
    if (!this.saving) this.editing = null;
  }

  save() {
    const p = this.editing;
    if (!p || this.saving) return;
    if (!p.name.trim()) {
      this.formError = 'Enter the partner name.';
      return;
    }
    this.saving = true;
    this.formError = '';
    const req = p.id ? this.service.update(p.id, p) : this.service.create(p);
    req.subscribe({
      next: () => {
        this.saving = false;
        this.alertService.alert(p.id ? 'Partner updated' : 'Partner added', 'success');
        this.editing = null;
        this.load();
      },
      error: (e) => {
        this.saving = false;
        this.formError = e?.error?.message || 'Could not save the partner';
      },
    });
  }

  toggleActive(p: DeliveryPartner) {
    if (!p.id) return;
    const next = { ...p, active: !p.active };
    this.service.update(p.id, next).subscribe({
      next: () => {
        p.active = next.active;
        this.alertService.alert(`${p.name} ${p.active ? 'switched on' : 'switched off'}`, 'success');
      },
      error: (e) => this.alertService.alert(e?.error?.message || 'Could not update the partner', 'error'),
    });
  }

  askDelete(p: DeliveryPartner) {
    this.deleting = p;
  }

  confirmDelete() {
    const p = this.deleting;
    if (!p?.id || this.removing) return;
    this.removing = true;
    this.service.remove(p.id).subscribe({
      next: (r) => {
        this.removing = false;
        this.deleting = null;
        if (this.editing?.id === p.id) this.editing = null;
        this.alertService.alert(r.deactivated ? `${p.name} has orders, so it was switched off instead` : 'Partner deleted', 'success');
        this.load();
      },
      error: (e) => {
        this.removing = false;
        this.alertService.alert(e?.error?.message || 'Could not delete the partner', 'error');
      },
    });
  }
}
